import nacl from 'tweetnacl';
import { decodeBase64, encodeBase64 } from 'tweetnacl-util';
import Aes from 'react-native-aes-crypto';

import {
  parseWrappedKey,
  serializeWrappedKey,
  unwrapKey,
  wrapKey,
  WrappedKeyEnvelope,
} from './KeyWrapping';

/**
 * Step 5 trusted-party wrapping — X25519 + XSalsa20-Poly1305 sealed box.
 *
 * Why asymmetric? §7 of docs/KEY_WRAPPING_DESIGN.md decided option A (async
 * handshake). The invitee can't wrap the primary's master key until they've
 * enrolled their own recovery code, and the primary can't wrap-for-them until
 * they've seen the invitee's public key. Asymmetric wrapping breaks that
 * dependency: the invitee publishes a public half at accept time, the primary
 * wraps under it at "complete pending connection" time, and the invitee later
 * unwraps with their private half on first access.
 *
 * Construction follows the standard libsodium sealed-box pattern but with a
 * random 24-byte nonce instead of the BLAKE2b-derived one (tweetnacl doesn't
 * ship BLAKE2b and the security posture is equivalent: the ephemeral secret
 * key is never reused, so the nonce only needs to be distinct per message —
 * random bytes satisfy that).
 *
 *   ephemeral_keypair = nacl.box.keyPair()
 *   nonce             = randomBytes(24)
 *   box               = crypto_box(plaintext, nonce, recipient_pk, ephemeral_sk)
 *   sealed            = ephemeral_pk (32B) || nonce (24B) || box (16B tag + ciphertext)
 *
 * The invitee's private wrapping key is itself wrapped under their own KEK
 * via the existing KeyWrapping primitive — the backend never sees either
 * half of the wrapping keypair in cleartext, mirroring the primary master
 * key's storage model.
 */

const PUBLIC_KEY_BYTES = nacl.box.publicKeyLength;   // 32
const SECRET_KEY_BYTES = nacl.box.secretKeyLength;   // 32
const NONCE_BYTES = nacl.box.nonceLength;            // 24
const BOX_OVERHEAD_BYTES = nacl.box.overheadLength;  // 16 (Poly1305 tag)

const SEALED_VERSION_PREFIX = 'tpw1:';

export class TrustedPartyWrappingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TrustedPartyWrappingError';
  }
}

export interface WrappingKeypair {
  /** base64-encoded X25519 public key, safe to publish to the backend. */
  publicKey: string;
  /** base64-encoded X25519 secret key, MUST be wrapped before it leaves RAM. */
  secretKey: string;
}

/**
 * Fresh X25519 keypair. The caller is expected to wrap `secretKey` under the
 * user's KEK before uploading it; only `publicKey` is safe to publish.
 */
export async function generateWrappingKeypair(): Promise<WrappingKeypair> {
  const secretKey = await randomBytes(SECRET_KEY_BYTES);
  const kp = nacl.box.keyPair.fromSecretKey(secretKey);
  return {
    publicKey: encodeBase64(kp.publicKey),
    secretKey: encodeBase64(kp.secretKey),
  };
}

/**
 * Seal a master key (hex-encoded, typically 64 hex chars) for a single
 * recipient. Output is versioned so we can swap primitives later.
 *
 * The plaintext is the master key *bytes* decoded from the hex string —
 * that keeps the sealed payload compact (48 bytes for a 32-byte master
 * key: 32 pk + 24 nonce + (32 + 16) box). For anything that isn't a
 * hex string, throws.
 */
export async function sealForRecipient(
  plaintextHex: string,
  recipientPublicKeyBase64: string,
): Promise<string> {
  if (typeof plaintextHex !== 'string' || plaintextHex.length === 0) {
    throw new TrustedPartyWrappingError('plaintext must be a non-empty hex string');
  }
  if (!/^[0-9a-fA-F]+$/.test(plaintextHex) || plaintextHex.length % 2 !== 0) {
    throw new TrustedPartyWrappingError('plaintext must be hex-encoded');
  }

  let recipientPk: Uint8Array;
  try {
    recipientPk = decodeBase64(recipientPublicKeyBase64);
  } catch {
    throw new TrustedPartyWrappingError('recipient public key is not valid base64');
  }
  if (recipientPk.length !== PUBLIC_KEY_BYTES) {
    throw new TrustedPartyWrappingError(
      `recipient public key must be ${PUBLIC_KEY_BYTES} bytes`,
    );
  }

  const plaintext = hexToBytes(plaintextHex);
  const ephemeralSecretKey = await randomBytes(SECRET_KEY_BYTES);
  const ephemeral = nacl.box.keyPair.fromSecretKey(ephemeralSecretKey);
  const nonce = await randomBytes(NONCE_BYTES);
  const box = nacl.box(plaintext, nonce, recipientPk, ephemeral.secretKey);

  // 32 (pk) + 24 (nonce) + (plaintext + 16 overhead)
  const sealed = new Uint8Array(
    PUBLIC_KEY_BYTES + NONCE_BYTES + box.length,
  );
  sealed.set(ephemeral.publicKey, 0);
  sealed.set(nonce, PUBLIC_KEY_BYTES);
  sealed.set(box, PUBLIC_KEY_BYTES + NONCE_BYTES);

  return SEALED_VERSION_PREFIX + encodeBase64(sealed);
}

/**
 * Open a sealed master key using the recipient's (unwrapped) secret key.
 * Throws on any tampering, nonce reuse, or length mismatch — same E2E
 * posture as KeyWrapping.unwrapKey.
 *
 * Returns the hex-encoded master key. Callers can hand it directly to
 * DocumentEncryptionV2 / MasterKeyService flows without further encoding.
 */
export function openSealedMasterKey(
  sealed: string,
  mySecretKeyBase64: string,
): string {
  if (typeof sealed !== 'string' || !sealed.startsWith(SEALED_VERSION_PREFIX)) {
    throw new TrustedPartyWrappingError('sealed payload version mismatch');
  }
  const body = sealed.slice(SEALED_VERSION_PREFIX.length);

  let sealedBytes: Uint8Array;
  try {
    sealedBytes = decodeBase64(body);
  } catch {
    throw new TrustedPartyWrappingError('sealed payload is not valid base64');
  }

  const minLen = PUBLIC_KEY_BYTES + NONCE_BYTES + BOX_OVERHEAD_BYTES;
  if (sealedBytes.length < minLen) {
    throw new TrustedPartyWrappingError('sealed payload truncated');
  }

  const ephemeralPk = sealedBytes.subarray(0, PUBLIC_KEY_BYTES);
  const nonce = sealedBytes.subarray(
    PUBLIC_KEY_BYTES,
    PUBLIC_KEY_BYTES + NONCE_BYTES,
  );
  const box = sealedBytes.subarray(PUBLIC_KEY_BYTES + NONCE_BYTES);

  let mySk: Uint8Array;
  try {
    mySk = decodeBase64(mySecretKeyBase64);
  } catch {
    throw new TrustedPartyWrappingError('secret key is not valid base64');
  }
  if (mySk.length !== SECRET_KEY_BYTES) {
    throw new TrustedPartyWrappingError(
      `secret key must be ${SECRET_KEY_BYTES} bytes`,
    );
  }

  const opened = nacl.box.open(box, nonce, ephemeralPk, mySk);
  if (!opened) {
    throw new TrustedPartyWrappingError('sealed payload failed authentication');
  }
  return bytesToHex(opened);
}

/**
 * Wrap the wrapping-secret-key under the user's KEK for persistence. Output
 * is a `WrappedKeyEnvelope` (same envelope format the master key uses) so
 * the backend just stores opaque JSON.
 *
 * The caller must derive `kek` from the user's secret code via
 * KeyWrapping.deriveKEK — this function has no opinion on how the KEK was
 * derived, it just consumes it.
 */
export async function wrapWrappingSecretKey(
  secretKeyBase64: string,
  kek: string,
): Promise<string> {
  if (typeof secretKeyBase64 !== 'string' || secretKeyBase64.length === 0) {
    throw new TrustedPartyWrappingError('secret key is required');
  }
  const envelope = await wrapKey(secretKeyBase64, kek);
  return serializeWrappedKey(envelope);
}

/**
 * Unwrap the wrapping-secret-key after a new login. Mirrors
 * MasterKeyService.fetchAndUnwrapMasterKey — caller holds the KEK and hands
 * the wrapped envelope in; throws on tag mismatch / wrong secret code.
 */
export async function unwrapWrappingSecretKey(
  wrappedSerialized: string,
  kek: string,
): Promise<string> {
  if (typeof wrappedSerialized !== 'string' || wrappedSerialized.length === 0) {
    throw new TrustedPartyWrappingError('wrapped secret key is required');
  }
  let envelope: WrappedKeyEnvelope;
  try {
    envelope = parseWrappedKey(wrappedSerialized);
  } catch (err) {
    // Re-surface as our error type so callers can catch a single class.
    throw new TrustedPartyWrappingError(
      `wrapped secret key envelope invalid: ${(err as Error).message}`,
    );
  }
  return unwrapKey(envelope, kek);
}

// --- byte helpers ------------------------------------------------------

function hexToBytes(hex: string): Uint8Array {
  const normalized = hex.toLowerCase();
  const out = new Uint8Array(normalized.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(normalized.substr(i * 2, 2), 16);
  }
  return out;
}

function bytesToHex(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i++) {
    out += bytes[i].toString(16).padStart(2, '0');
  }
  return out;
}

async function randomBytes(length: number): Promise<Uint8Array> {
  return hexToBytes(await Aes.randomKey(length));
}

// Re-exports for test harnesses / callers that need direct access.
export const __internal = {
  hexToBytes,
  bytesToHex,
  randomBytes,
  SEALED_VERSION_PREFIX,
  PUBLIC_KEY_BYTES,
  SECRET_KEY_BYTES,
  NONCE_BYTES,
  BOX_OVERHEAD_BYTES,
};
